/**
 * ONE control for "this screen is now in a different mode".
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * By 8 Sep the hub had three of these and two designs. The feed's "View as
 * employee" and "Moderator mode" were pill buttons with a status dot
 * (`.viewas`); Wiki One's "Curator mode" was a bare checkbox and a label. Same
 * act, same consequence — the screen stops showing you what everybody else
 * sees — and a person moving between the two screens had to learn it twice.
 *
 * The feed's design won because it was already right: a pill that visibly
 * CHANGES when on, with a dot that fills, so "am I in this mode" is answerable
 * from across the room. A checkbox states a preference; this states a state.
 *
 * ── THE TONE IS NOT DECORATION ──────────────────────────────────────────────
 *
 * Three tones, and which one a mode gets is a claim about what it can do:
 *
 *   preview  amber. You are looking at something other than your own view.
 *            Nothing you can do here is different. "View as employee".
 *   work     amber, but the mode surfaces WORK — unapproved drafts, queues,
 *            filters. Reversible, nothing destroyed. "Curator mode".
 *   danger   red. Arming this puts an irreversible control on every row.
 *            "Moderator mode", which carries Remove.
 *
 * preview and work share amber deliberately: both mean "unfinished or
 * unapproved", which is what amber means everywhere else in this palette. Red
 * is reserved for the one that can destroy something. **Do not give a new mode
 * `danger` because it feels important** — give it danger only if being in it
 * makes an irreversible action reachable.
 *
 * ── IT IS NEVER A PERMISSION ────────────────────────────────────────────────
 *
 * Every mode in this app is presentation. RLS decides what may be read and the
 * SECURITY DEFINER functions decide what may be written; a client toggle can
 * neither widen nor narrow either (rule 3). The caller renders this only for
 * people who hold the relevant permission — that is a courtesy to everyone
 * else, not a boundary, and nothing downstream may rely on it.
 */
export type ModeTone = 'preview' | 'work' | 'danger'

export function ModeSwitch({
  label,
  on,
  onToggle,
  tone = 'work',
  title,
}: {
  /** The mode's name, e.g. "Curator mode". The component appends " — on". */
  label: string
  on: boolean
  onToggle: (next: boolean) => void
  tone?: ModeTone
  /** What being in this mode actually does. Worth writing: the label names
   *  the mode and only this says what changes. */
  title?: string
}) {
  return (
    <button
      type="button"
      className={`modeswitch modeswitch--${tone}${on ? ' modeswitch--on' : ''}`}
      onClick={() => onToggle(!on)}
      aria-pressed={on}
      title={title}
    >
      <span className="modeswitch__dot" aria-hidden="true" />
      {/* "— on" rather than a separate badge: the label and its state read as
          one phrase, and a pill that only changes colour is invisible to
          anybody who cannot rely on colour. */}
      {on ? `${label} — on` : label}
    </button>
  )
}
